import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ========== SBOM Generation ==========

export const generateSBOM = async (data) => {
  const response = await api.post('/api/litesabre/sbom/generate', data);
  return response.data;
};

export const getSBOMFromScan = async (scanId) => {
  const response = await api.get(`/api/litesabre/sbom/from-scan/${scanId}`);
  return response.data;
};

// ========== SBOM Reports ==========

export const getSBOMReports = async (params = {}) => {
  const response = await api.get('/api/litesabre/sbom/reports', { params });
  return response.data;
};

export const getSBOMReport = async (sbomId) => {
  const response = await api.get(`/api/litesabre/sbom/reports/${sbomId}`);
  return response.data;
};

export const deleteSBOMReport = async (sbomId) => {
  const response = await api.delete(`/api/litesabre/sbom/reports/${sbomId}`);
  return response.data;
};

// ========== SBOM Statistics ==========

export const getSBOMStats = async () => {
  const response = await api.get('/api/litesabre/sbom/stats');
  return response.data;
};

// ========== SBOM Export ==========

export const exportSBOMCycloneDX = async (sbomId) => {
  const response = await api.get(`/api/litesabre/sbom/reports/${sbomId}/export/cyclonedx`, {
    responseType: 'blob'
  });
  
  downloadFile(response.data, `sbom-${sbomId}-cyclonedx.json`, 'application/json');
  return true;
};

export const exportSBOMSPDX = async (sbomId) => {
  const response = await api.get(`/api/litesabre/sbom/reports/${sbomId}/export/spdx`, {
    responseType: 'blob'
  });
  
  downloadFile(response.data, `sbom-${sbomId}-spdx.json`, 'application/json');
  return true;
};

export const exportSBOMCSV = async (sbomId) => {
  const response = await api.get(`/api/litesabre/sbom/reports/${sbomId}/export/csv`, {
    responseType: 'blob'
  });
  
  downloadFile(response.data, `sbom-${sbomId}.csv`, 'text/csv');
  return true;
};

export const exportSBOMJSON = async (sbomId) => {
  const response = await api.get(`/api/litesabre/sbom/reports/${sbomId}/export/json`, {
    responseType: 'blob'
  });
  
  downloadFile(response.data, `sbom-${sbomId}.json`, 'application/json');
  return true;
};

export const exportSBOMPDF = async (sbomId) => {
  const response = await api.get(`/api/litesabre/sbom/reports/${sbomId}/export/pdf`, {
    responseType: 'blob'
  });
  
  downloadFile(response.data, `sbom-${sbomId}.pdf`, 'application/pdf');
  return true;
};

// ========== SBOM Rescan ==========

export const rescanSBOM = async (sbomId) => {
  const response = await api.post(`/api/litesabre/sbom/reports/${sbomId}/rescan`);
  return response.data;
};

// ========== SBOM Security Overview ==========

export const getSBOMSecurityOverview = async (sbomId) => {
  const response = await api.get(`/api/litesabre/sbom/reports/${sbomId}/security-overview`);
  return response.data;
};

// ========== SBOM License Analysis ==========

export const getSBOMLicenseAnalysis = async (sbomId) => {
  const response = await api.get(`/api/litesabre/sbom/reports/${sbomId}/license-analysis`);
  return response.data;
};

// ========== SBOM Vulnerability Scan ==========

export const scanSBOMVulnerabilities = async (sbomId, sources = ['nvd', 'osv', 'github_advisory']) => {
  const response = await api.post(`/api/litesabre/sbom/reports/${sbomId}/vulnerability-scan`, null, {
    params: { sources: sources.join(',') }
  });
  return response.data;
};

// ========== Helper Functions ==========

const downloadFile = (data, filename, mimeType) => {
  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const getHealthScoreColor = (score) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  if (score >= 40) return 'text-orange-600';
  return 'text-red-600';
};

export const getHealthScoreBg = (score) => {
  if (score >= 80) return 'bg-green-100';
  if (score >= 60) return 'bg-yellow-100';
  if (score >= 40) return 'bg-orange-100';
  return 'bg-red-100';
};

export const getMaintenanceStatusColor = (status) => {
  const colors = {
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-yellow-100 text-yellow-700',
    deprecated: 'bg-red-100 text-red-700',
    unknown: 'bg-gray-100 text-gray-700'
  };
  return colors[status] || colors.unknown;
};

export const getVulnerabilityBadge = (severity, count) => {
  if (count === 0) return null;
  
  const badges = {
    critical: 'bg-red-600 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-black',
    low: 'bg-blue-500 text-white'
  };
  
  return badges[severity] || 'bg-gray-500 text-white';
};

export const formatNumber = (num) => {
  if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

export const getEcosystemIcon = (ecosystem) => {
  const icons = {
    npm: '📦',
    pypi: '🐍',
    maven: '☕',
    go: '🐹',
    cargo: '🦀',
    rubygems: '💎',
    nuget: '🔷',
    composer: '🎼'
  };
  return icons[ecosystem] || '📦';
};

export const getEcosystemColor = (ecosystem) => {
  const colors = {
    npm: 'bg-red-100 text-red-700 border-red-200',
    pypi: 'bg-blue-100 text-blue-700 border-blue-200',
    maven: 'bg-orange-100 text-orange-700 border-orange-200',
    go: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    cargo: 'bg-amber-100 text-amber-700 border-amber-200',
    rubygems: 'bg-pink-100 text-pink-700 border-pink-200',
    nuget: 'bg-purple-100 text-purple-700 border-purple-200',
    composer: 'bg-indigo-100 text-indigo-700 border-indigo-200'
  };
  return colors[ecosystem] || 'bg-gray-100 text-gray-700 border-gray-200';
};

export default {
  generateSBOM,
  getSBOMFromScan,
  getSBOMReports,
  getSBOMReport,
  deleteSBOMReport,
  getSBOMStats,
  exportSBOMCycloneDX,
  exportSBOMSPDX,
  exportSBOMCSV,
  exportSBOMJSON,
  exportSBOMPDF,
  rescanSBOM,
  getSBOMSecurityOverview,
  getSBOMLicenseAnalysis,
  scanSBOMVulnerabilities,
  getHealthScoreColor,
  getHealthScoreBg,
  getMaintenanceStatusColor,
  getVulnerabilityBadge,
  formatNumber,
  getEcosystemIcon,
  getEcosystemColor
};
